/**
 * GoalCard — one goal rendered as a §7 card (Epic 18 / Goals grid view).
 *
 * A faithful visual mirror of the ClientCard markup inside ClientCardGrid.tsx:
 * same card chrome (!rounded-[22px], inline 0.5px border, shadow-xs /
 * hover:shadow-sm transition-shadow), the same header row
 * (flex items-center gap-3 px-4 pt-4 pb-3), the same divider, the same body
 * spacing (pt-3 pb-3 px-4 space-y-3), and the same 3-dot DropdownMenu footer.
 *
 * Adapted slots for a goal:
 *  - name block  → goal.name (header — no client avatar; the client icon lives
 *                  only with the client name in the body)
 *  - right meta  → status badge (On Track / Off Track / Complete / Paused)
 *  - body        → client (32px ClientAvatar + name, matching the table) +
 *                  platform logo + metric, current/target value line, progress
 *                  bar, interval text, created/updated dates + menu
 *
 * 🔴 Status logic is NOT recomputed here. displayStatus / GOAL_STATUS_BADGES /
 * INTERVAL_BADGES come verbatim from lib/goalPageDisplay.ts (the page's badge
 * truth — paused wins, else the fresh derived status). The progress bar reuses
 * the table's exact fill rendering via clampBarFill (lib/goalFormat.ts). Menu
 * actions = Edit / Pause|Resume / Delete, identical to GoalsTable's menu.
 */

import {
  Card,
  CardContent,
  Button,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@provenleap/ui";
import {
  DotsThreeVertical,
  PencilSimple,
  Pause,
  Play,
  Trash,
} from "@phosphor-icons/react";
import { ClientAvatar } from "../clients/ClientAvatar";
import { PICKER_PLATFORMS, logoUrl } from "../../lib/integrations";
import {
  displayStatus,
  GOAL_STATUS_BADGES,
  INTERVAL_BADGES,
  titleCaseStatus,
  type GoalListItem,
} from "../../lib/goalPageDisplay";
import { GOAL_STATUS_ICONS } from "./goalStatusIcons";
import { formatGoalValue, clampBarFill } from "../../lib/goalFormat";

export interface GoalCardProps {
  goal: GoalListItem;
  canManage: boolean;
  /** Optional click target — toggles the inline detail / opens a panel. */
  onGoalClick?: (goal: GoalListItem) => void;
  onEdit?: (goal: GoalListItem) => void;
  onTogglePause?: (goal: GoalListItem) => void;
  onDelete?: (goal: GoalListItem) => void;
}

/** The platform's display name from PICKER_PLATFORMS (falls back to the raw key). */
function platformDisplayName(platform: string): string {
  return PICKER_PLATFORMS.find((p) => p.platform === platform)?.displayName ?? platform;
}

/** Humanize a metric key (same inlined logic as GoalsTable's Metric column). */
function humanizeMetricKey(key: string): string {
  return key
    .replace(/[_-]+/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}

/** Same platform-logo rendering as GoalsTable (h-4 w-4 rounded-sm). */
function platformLogo(platform: string | null) {
  if (!platform) return null;
  const match = PICKER_PLATFORMS.find((p) => p.platform === platform);
  if (!match?.domain) return null;
  return (
    <img
      src={logoUrl(match.domain)}
      alt={match.displayName}
      title={match.displayName}
      className="h-4 w-4 rounded-sm object-contain"
      loading="lazy"
    />
  );
}

const DATE_FMT: Intl.DateTimeFormatOptions = {
  month: "short",
  day: "numeric",
  year: "numeric",
};

export function GoalCard({
  goal,
  canManage,
  onGoalClick,
  onEdit,
  onTogglePause,
  onDelete,
}: GoalCardProps) {
  const status = displayStatus(goal);
  const badge = GOAL_STATUS_BADGES[status];
  const StatusIcon = GOAL_STATUS_ICONS[status];
  const pct = goal.progress.percentage;
  const fill = clampBarFill(pct);
  const valueLabel = `${formatGoalValue(goal.currentValue, goal.unit, goal.currencyCode)} / ${formatGoalValue(goal.targetValue, goal.unit, goal.currencyCode)}`;

  return (
    <Card
      className={`shadow-xs hover:shadow-sm transition-shadow ${onGoalClick ? "cursor-pointer" : ""} !rounded-[22px]`}
      style={{ border: "0.5px solid var(--hairline)" }}
      onClick={onGoalClick ? () => onGoalClick(goal) : undefined}
      role={onGoalClick ? "link" : undefined}
      tabIndex={onGoalClick ? 0 : undefined}
      onKeyDown={
        onGoalClick
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onGoalClick(goal);
              }
            }
          : undefined
      }
      data-testid={`goal-card-${goal.id}`}
    >
      <CardContent className="p-0">
        {/* Header row — goal name + status badge (no client avatar here; the
            client icon belongs only with the client name in the body) */}
        <div className="flex items-center gap-3 px-4 pt-4 pb-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="text-[14px] font-medium truncate min-w-0" title={goal.name}>
                {goal.name}
              </h3>
            </div>
          </div>
          <span
            className={`shrink-0 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${badge.badge}`}
            data-testid={`goal-status-${goal.id}`}
          >
            <StatusIcon size={12} weight="bold" />
            {titleCaseStatus(badge.label)}
          </span>
        </div>

        <div className="mx-4" style={{ borderTop: "0.5px solid hsl(var(--input) / 0.5)" }} />

        {/* Body */}
        <div className="pt-3 pb-3 px-4 space-y-3">
          {/* Client line — 32px avatar + name (same size as the table) */}
          <div className="flex items-center gap-2 text-sm text-foreground">
            <ClientAvatar name={goal.clientName} logoUrl={null} size="sm" />
            <span className="truncate" title={goal.clientName}>
              {goal.clientName}
            </span>
          </div>

          {/* Platform (logo + name) + metric — mirrors the table's columns,
              color (foreground) and size (text-sm) */}
          {(goal.platform || goal.metricKey) && (
            <div
              className="flex items-center gap-1.5 text-sm text-foreground"
              data-testid={`goal-platform-metric-${goal.id}`}
            >
              {platformLogo(goal.platform)}
              {goal.platform && (
                <span className="truncate" title={platformDisplayName(goal.platform)}>
                  {platformDisplayName(goal.platform)}
                </span>
              )}
              {goal.platform && goal.metricKey && <span className="opacity-40">·</span>}
              {goal.metricKey && (
                <span className="truncate" title={humanizeMetricKey(goal.metricKey)}>
                  {humanizeMetricKey(goal.metricKey)}
                </span>
              )}
            </div>
          )}

          {/* Current / target value + interval on one row */}
          <div className="flex items-center justify-between gap-2 text-[13px] text-foreground">
            <span data-testid={`goal-value-${goal.id}`}>{valueLabel}</span>
            <span className="text-sm">{INTERVAL_BADGES[goal.periodType]}</span>
          </div>

          {/* Progress bar — same fill rendering as GoalsTable */}
          <div className="flex items-center gap-2">
            <div
              className="h-2 w-full overflow-hidden rounded-full bg-muted"
              style={{ border: "0.5px solid var(--hairline)" }}
              title={valueLabel}
            >
              <div
                className={`h-full rounded-full ${badge.bar}`}
                style={{ width: `${fill}%` }}
                data-testid={`goal-bar-${goal.id}`}
              />
            </div>
            <span className="text-[12px] text-foreground tabular-nums shrink-0">
              {Math.round(pct)}%
            </span>
          </div>

          {/* Footer — created/updated dates + 3-dot menu */}
          <div className="flex items-center gap-3 text-[13px] text-foreground -mt-1">
            {goal.createdAt != null && (
              <span>
                Created {new Date(goal.createdAt).toLocaleDateString("en-US", DATE_FMT)}
              </span>
            )}
            {goal.createdAt != null && goal.updatedAt != null && (
              <span className="w-px h-3.5" style={{ background: "hsl(var(--input) / 0.3)" }} />
            )}
            {goal.updatedAt != null && (
              <span>
                Updated {new Date(goal.updatedAt).toLocaleDateString("en-US", DATE_FMT)}
              </span>
            )}
            {canManage && (
              <div
                className="ml-auto"
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
              >
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="min-h-8 min-w-8 cursor-pointer rounded-lg border-[0.5px] border-transparent hover:bg-background hover:border-input transition-all"
                      aria-label={`Actions for ${goal.name}`}
                    >
                      <DotsThreeVertical size={22} weight="bold" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="end"
                    className="rounded-xl p-2 shadow-md"
                    style={{ border: "0.5px solid var(--hairline)" }}
                  >
                    <DropdownMenuItem
                      className="min-h-11 text-[13px] cursor-pointer rounded-lg"
                      onClick={() => onEdit?.(goal)}
                    >
                      <PencilSimple size={14} className="mr-1" />
                      Edit
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className="min-h-11 text-[13px] cursor-pointer rounded-lg"
                      onClick={() => onTogglePause?.(goal)}
                    >
                      {goal.status === "paused" ? (
                        <>
                          <Play size={14} className="mr-1" />
                          Resume
                        </>
                      ) : (
                        <>
                          <Pause size={14} className="mr-1" />
                          Pause
                        </>
                      )}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      className="min-h-11 text-[13px] text-destructive cursor-pointer rounded-lg hover:!bg-destructive/10 focus:!bg-destructive/10 focus:!text-destructive focus:!border-destructive/30"
                      onClick={() => onDelete?.(goal)}
                    >
                      <Trash size={14} className="mr-1" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
